/**
 * Minimal vector PDF writer — one page of lines (solid/dashed/colored) and
 * Helvetica text. No dependencies; enough for a drawing sheet. PDF user
 * space is points (1/72"), Y up; we scale mm → pt.
 */

import type { SheetModel } from '../drawing/layout';

const MM = 72 / 25.4;

export function sheetToPdf(model: SheetModel): Blob {
  const W = model.width * MM;
  const H = model.height * MM;

  // Content stream: sheet mm (Y up) → pt via the cm matrix.
  const c: string[] = [`${MM.toFixed(4)} 0 0 ${MM.toFixed(4)} 0 0 cm`, '0.35 w', '1 J', '1 j'];
  const stroke = (
    segs: number[],
    opts: { rgb: [number, number, number]; dash?: string; width?: number },
  ): void => {
    if (segs.length < 4) return;
    c.push(`${opts.rgb.map((v) => v.toFixed(3)).join(' ')} RG`);
    c.push(opts.dash ? `[${opts.dash}] 0 d` : '[] 0 d');
    c.push(`${(opts.width ?? 0.25).toFixed(2)} w`);
    for (let i = 0; i + 3 < segs.length; i += 4) {
      c.push(
        `${n(segs[i]!)} ${n(segs[i + 1]!)} m ${n(segs[i + 2]!)} ${n(segs[i + 3]!)} l`,
      );
    }
    c.push('S');
  };
  stroke(model.frame, { rgb: [0.2, 0.2, 0.35], width: 0.35 });
  stroke(model.hatch, { rgb: [0.55, 0.55, 0.55], width: 0.12 });
  stroke(model.hidden, { rgb: [0.45, 0.45, 0.45], dash: '1.6 1.1', width: 0.18 });
  stroke(model.visible, { rgb: [0, 0, 0], width: 0.35 });
  stroke(model.section, { rgb: [0.75, 0.1, 0.1], width: 0.5 });
  stroke(model.dims, { rgb: [0.1, 0.35, 0.65], width: 0.16 });

  c.push('0.1 0.1 0.1 rg');
  for (const t of model.texts) {
    const text = t.text.replace('⌀', '\\330'); // Ø in WinAnsi
    const esc = text.replace(/\\(?!330)/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
    // Approximate centering: Helvetica ≈ 0.5em average advance.
    const w = t.text.length * t.size * 0.5;
    const x = t.anchor === 'middle' ? t.p[0] - w / 2 : t.anchor === 'end' ? t.p[0] - w : t.p[0];
    c.push('BT', `/F1 ${n(t.size)} Tf`, `${n(x)} ${n(t.p[1])} Td`, `(${esc}) Tj`, 'ET');
  }
  const content = c.join('\n');

  const objects: string[] = [];
  objects.push('<< /Type /Catalog /Pages 2 0 R >>');
  objects.push('<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  objects.push(
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W.toFixed(2)} ${H.toFixed(2)}] ` +
      '/Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
  );
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  objects.push(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((obj, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const off of offsets) pdf += `${String(off).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new Blob([pdf], { type: 'application/pdf' });
}

const n = (v: number): string => (Math.round(v * 100) / 100).toString();
